// ═══════════════════════════════════════════════════════════════════════════════
// Employer — medication reminder task group wizard (create & edit)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  MAX_MEDICATION_REMINDER_SLOTS,
  RECURRENCE_TYPE,
  USER_ROLE,
  buildMedicationReminderAutoTitle,
  resolveTaskGroupTitleLocale,
  type MedicationReminderTaskConfig,
} from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../components/ui/ToastProvider';
import { ApiClientError, taskGroupApi } from '../../lib/api';
import { isLimitReachedError } from '../../lib/limitError';
import { getHelperTodayYmd } from '../../lib/userTodayYmd';
import { mapApiClientErrorToMessage } from '../../i18n/mapApiClientError';
import { PersonNameSuggestionInput } from '../../components/employer/PersonNameSuggestionInput';
import {
  TaskGroupScheduleFields,
  type TaskGroupScheduleState,
} from '../../components/employer/TaskGroupScheduleFields';
import { AppButton } from '../../components/ui/AppButton';
import { AppModal } from '../../components/ui/AppModal';
import { TaskGroupWizardProgress } from '../../components/employer/TaskGroupWizardProgress';
import { InventoryWizardReviewStep } from '../../components/employer/InventoryWizardReviewStep';
import { buildMedicationReminderReviewRows } from '../../components/employer/taskGroupWizardReview';
import { NativeTimeInput } from '../../components/NativeTimeInput';
import { HelperDueReminderLimitationModal } from '../../components/employer/HelperDueReminderLimitationModal';
import { getHelperDueReminderLimitationCopy } from '../../components/employer/HelperDueReminderCheckbox';

const TOTAL_STEPS = 5;

export const EmployerMedicationReminderTaskPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { showSuccessToast, showErrorToast } = useToast();
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId?: string }>();
  const isEdit = Boolean(groupId);

  const [wizardStep, setWizardStep] = useState(1);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [personName, setPersonName] = useState('');
  const [description, setDescription] = useState('');
  const [medicationTimes, setMedicationTimes] = useState<string[]>(['']);
  const [sharedTitleOverride, setSharedTitleOverride] = useState('');
  const [titleModalOpen, setTitleModalOpen] = useState(false);
  const [titleModalDraft, setTitleModalDraft] = useState('');
  const [titleChoiceModalOpen, setTitleChoiceModalOpen] = useState(false);
  const [step3ReminderChoiceMade, setStep3ReminderChoiceMade] = useState(false);
  const [step3ReminderAskOpen, setStep3ReminderAskOpen] = useState(false);
  const [originalPersonName, setOriginalPersonName] = useState('');
  const [originalSharedTitle, setOriginalSharedTitle] = useState('');
  const [schedule, setSchedule] = useState<TaskGroupScheduleState>({
    recurrence: RECURRENCE_TYPE.NONE,
    recurrenceDays: [],
    monthlyKind: null,
    recurrenceMonthDays: [],
    recurrenceOrdinal: null,
    occurrenceDate: '',
    startDate: '',
    endDate: '',
    exclusionRanges: [],
    requirePhoto: true,
    notifyEmployer: true,
    remindHelperBeforeDue: false,
  });

  const todayYmd = useMemo(() => getHelperTodayYmd(user?.settings?.timezone), [user?.settings?.timezone]);
  const autoTitle = useMemo(
    () => buildMedicationReminderAutoTitle({ locale: resolveTaskGroupTitleLocale(language), personName }),
    [language, personName],
  );
  const resolvedTitle = sharedTitleOverride.trim() !== '' ? sharedTitleOverride.trim() : autoTitle;
  const normalizedTimes = useMemo(
    () => medicationTimes.map((time) => time.trim()).filter((time) => time !== ''),
    [medicationTimes],
  );

  const medicationReviewRows = useMemo(() => {
    const rows = buildMedicationReminderReviewRows({
      t,
      personName,
      description,
      resolvedTitle,
      medicationTimes: normalizedTimes,
      schedule,
    });
    return rows.map((row) =>
      row.id === 'rev-title'
        ? {
            ...row,
            onEdit: () => setTitleModalOpen(true),
            editAriaLabel: t('task.medication.editTitleAria'),
          }
        : row,
    );
  }, [t, personName, description, resolvedTitle, normalizedTimes, schedule]);

  const loadGroup = useCallback(async () => {
    if (!groupId) {
      return;
    }
    setLoading(true);
    try {
      const res = await taskGroupApi.get(groupId);
      if (!res.success || !res.data) {
        showErrorToast(t('task.medication.loadError'));
        return;
      }
      const cfg = res.data.taskConfigs as MedicationReminderTaskConfig;
      setPersonName(cfg.personName);
      setOriginalPersonName(cfg.personName);
      setDescription(cfg.description);
      setMedicationTimes(cfg.medicationSlots.map((s) => s.time));
      setSharedTitleOverride(cfg.sharedTitle ?? '');
      const cfgAutoTitle = buildMedicationReminderAutoTitle({
        locale: resolveTaskGroupTitleLocale(language),
        personName: cfg.personName,
      });
      setOriginalSharedTitle(res.data.tasks.find((row) => row.status === 'active')?.title ?? cfgAutoTitle);
      setSchedule({
        recurrence: cfg.recurrence,
        recurrenceDays: cfg.recurrenceDays ?? [],
        monthlyKind: cfg.monthlyKind ?? null,
        recurrenceMonthDays: cfg.recurrenceMonthDays ?? [],
        recurrenceOrdinal: cfg.recurrenceOrdinal ?? null,
        occurrenceDate: cfg.occurrenceDate,
        startDate: cfg.startDate,
        endDate: cfg.endDate,
        exclusionRanges: cfg.exclusionRanges,
        requirePhoto: cfg.requirePhoto,
        notifyEmployer: cfg.notifyEmployer,
        remindHelperBeforeDue: cfg.remindHelperBeforeDue,
      });
    } catch {
      showErrorToast(t('task.medication.loadError'));
    } finally {
      setLoading(false);
    }
  }, [groupId, language, showErrorToast, t]);

  useEffect(() => {
    if (isEdit) {
      void loadGroup();
    }
  }, [isEdit, loadGroup]);

  useEffect(() => {
    if (schedule.occurrenceDate === '' && todayYmd !== '' && !isEdit) {
      setSchedule((prev) => ({ ...prev, occurrenceDate: todayYmd, startDate: todayYmd }));
    }
  }, [isEdit, schedule.occurrenceDate, todayYmd]);

  useEffect(() => {
    if (!titleModalOpen) {
      return;
    }
    setTitleModalDraft(sharedTitleOverride.trim() !== '' ? sharedTitleOverride : autoTitle);
  }, [titleModalOpen, sharedTitleOverride, autoTitle]);

  if (!user || user.role !== USER_ROLE.EMPLOYER) {
    return (
      <div className="flex min-h-full items-center justify-center px-4">
        <p role="alert">{t('task.inventory.employerOnly')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  const validateStep = (): string | null => {
    if (wizardStep === 1 && personName.trim() === '') {
      return t('task.medication.personRequired');
    }
    if (wizardStep === 3) {
      if (normalizedTimes.length === 0) {
        return t('task.medication.timeRequired');
      }
      if (new Set(normalizedTimes).size !== normalizedTimes.length) {
        return t('task.medication.timesUnique');
      }
      if (normalizedTimes.length > MAX_MEDICATION_REMINDER_SLOTS) {
        return t('task.medication.timeMax');
      }
    }
    if (wizardStep === 4) {
      if (schedule.recurrence === RECURRENCE_TYPE.NONE && schedule.occurrenceDate === '') {
        return t('task.inventory.dueDateInvalid');
      }
      if (schedule.recurrence !== RECURRENCE_TYPE.NONE && schedule.startDate === '') {
        return t('task.form.startDateLabel');
      }
      if (
        (schedule.recurrence === RECURRENCE_TYPE.WEEKLY ||
          schedule.recurrence === RECURRENCE_TYPE.BIWEEKLY) &&
        schedule.recurrenceDays.length === 0
      ) {
        return t('task.form.recurrenceWeekdaysRequired');
      }
      if (schedule.recurrence === RECURRENCE_TYPE.MONTHLY) {
        if (schedule.monthlyKind === 'day' && schedule.recurrenceMonthDays.length === 0) {
          return t('task.form.recurrenceMonthDaysRequired');
        }
        if (
          (schedule.monthlyKind === 'nth_weekday' || schedule.monthlyKind === 'last_weekday') &&
          schedule.recurrenceDays.length === 0
        ) {
          return t('task.form.recurrenceWeekdaysRequired');
        }
      }
    }
    return null;
  };

  const goReview = () => {
    if (isEdit && personName.trim() !== originalPersonName.trim()) {
      setTitleChoiceModalOpen(true);
      return;
    }
    setWizardStep(5);
  };

  const handleAddTime = () => {
    if (medicationTimes.length >= MAX_MEDICATION_REMINDER_SLOTS) {
      return;
    }
    setMedicationTimes((prev) => [...prev, '']);
  };

  const handleRemoveTime = (index: number) => {
    setMedicationTimes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        personName: personName.trim(),
        description: description.trim(),
        medicationTimes: normalizedTimes,
        sharedTitleOverride: sharedTitleOverride.trim() !== '' ? sharedTitleOverride.trim() : undefined,
        locale: resolveTaskGroupTitleLocale(language),
        ...schedule,
      };
      const res = isEdit && groupId
        ? await taskGroupApi.updateMedicationReminder(groupId, payload)
        : await taskGroupApi.createMedicationReminder(payload);
      if (res.success) {
        showSuccessToast(isEdit ? t('task.medication.updateSuccess') : t('task.medication.createSuccess'));
        navigate('/tasks', { replace: true });
        return;
      }
      showErrorToast(res.error ?? t('task.medication.createError'));
    } catch (caught) {
      if (isLimitReachedError(caught)) {
        return;
      }
      const msg =
        caught instanceof ApiClientError
          ? mapApiClientErrorToMessage(caught, t) ?? caught.message
          : t('task.medication.createError');
      showErrorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTitleModalConfirm = () => {
    setSharedTitleOverride(titleModalDraft.trim());
    setTitleModalOpen(false);
  };

  const handleTitleModalCancel = () => {
    setTitleModalOpen(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleBackKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleBack();
    }
  };

  const limitationCopy = getHelperDueReminderLimitationCopy(t, 'medication');

  const handleStep3ReminderYes = (): void => {
    setSchedule((prev) => ({ ...prev, remindHelperBeforeDue: true }));
    setStep3ReminderChoiceMade(true);
    setStep3ReminderAskOpen(false);
    setWizardStep(4);
  };

  const handleStep3ReminderNo = (): void => {
    setSchedule((prev) => ({ ...prev, remindHelperBeforeDue: false }));
    setStep3ReminderChoiceMade(true);
    setStep3ReminderAskOpen(false);
    setWizardStep(4);
  };

  const handleNextFromWizardStep = (): void => {
    const err = validateStep();
    if (err) {
      showErrorToast(err);
      return;
    }
    if (wizardStep === 4) {
      goReview();
      return;
    }
    if (wizardStep === 3 && !isEdit && !step3ReminderChoiceMade) {
      setStep3ReminderAskOpen(true);
      return;
    }
    setWizardStep((s) => s + 1);
  };

  return (
    <div className="min-h-full bg-gray-50 pb-24 dark:bg-gray-950">
      <header className="z-10 border-b border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto flex max-w-lg items-center gap-3 px-4 py-4">
          <button
            type="button"
            onClick={handleBack}
            onKeyDown={handleBackKeyDown}
            tabIndex={0}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label={t('task.action.back')}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {isEdit ? t('task.medication.editHeading') : t('task.medication.createHeading')}
          </h1>
        </div>
      </header>
      <main className="mx-auto max-w-lg space-y-4 px-4 py-6">
        <TaskGroupWizardProgress currentStep={wizardStep} totalSteps={TOTAL_STEPS} t={t} />
        {wizardStep < 5 ? (
        <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          {wizardStep === 1 ? (
            <PersonNameSuggestionInput id="med-person" label={t('task.medication.personLabel')} value={personName} onChange={setPersonName} placeholder={t('task.medication.personPlaceholder')} suggestHeading={t('task.medication.suggestHeading')} />
          ) : null}
          {wizardStep === 2 ? (
            <div>
              <label className="text-sm font-medium" htmlFor="med-desc">{t('task.form.descLabelOptional')}</label>
              <textarea id="med-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('task.medication.descPlaceholder')} rows={3} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800" />
            </div>
          ) : null}
          {wizardStep === 3 ? (
            <div className="space-y-3">
              <p className="text-sm font-medium">{t('task.medication.timesSectionLabel')}</p>
              {medicationTimes.map((time, index) => (
                <div key={`med-time-${index}`} className="flex gap-2">
                  <NativeTimeInput value={time} onChange={(e) => setMedicationTimes((prev) => prev.map((v, i) => (i === index ? e.target.value : v)))} wrapperClassName="flex-1" className="min-h-11 w-full rounded-lg border border-gray-300 px-3 dark:border-gray-600 dark:bg-gray-800" aria-label={t('task.medication.timeLabel', { n: index + 1 })} />
                  {medicationTimes.length > 1 ? (
                    <button type="button" onClick={() => handleRemoveTime(index)} className="min-h-11 rounded-lg border px-3 dark:border-gray-600" aria-label={t('task.medication.removeTimeAria')}>×</button>
                  ) : null}
                </div>
              ))}
              {medicationTimes.length < MAX_MEDICATION_REMINDER_SLOTS ? (
                <button type="button" onClick={handleAddTime} className="min-h-11 w-full rounded-lg border border-green-300 bg-green-50 py-2 text-sm font-medium text-green-800 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-300 dark:border-green-700 dark:bg-green-950/35 dark:text-green-200 dark:hover:bg-green-900/45">{t('task.medication.addTime')}</button>
              ) : null}
            </div>
          ) : null}
          {wizardStep === 4 ? (
            <TaskGroupScheduleFields
              t={t}
              schedule={schedule}
              onScheduleChange={(p) => setSchedule((s) => ({ ...s, ...p }))}
              todayYmd={todayYmd}
              isEdit={isEdit}
              helperReminderCopyContext="medication"
            />
          ) : null}
        </div>
        ) : null}
        {wizardStep === 5 ? (
          <div className="space-y-3">
            <InventoryWizardReviewStep
              cardHeading={t('task.group.wizard.reviewCardHeadingMedication')}
              rows={medicationReviewRows}
              backLabel={t('common.back')}
              primaryLabel={
                isEdit
                  ? t('task.group.wizard.reviewUpdateMedication')
                  : t('task.group.wizard.reviewConfirmMedication')
              }
              primaryBusyLabel={t('common.loading')}
              isPrimaryBusy={submitting}
              isBackDisabled={submitting}
              isPrimaryDisabled={submitting}
              onBack={() => setWizardStep(4)}
              onPrimary={() => void handleSubmit()}
            />
          </div>
        ) : null}
        {wizardStep < 5 ? (
        <div className="flex gap-3">
          {wizardStep > 1 ? (
            <AppButton type="button" variant="secondary" onClick={() => setWizardStep((s) => s - 1)} className="flex-1">
              {t('common.back')}
            </AppButton>
          ) : null}
          <AppButton type="button" variant="primary" onClick={handleNextFromWizardStep} className="flex-1">
            {t('common.next')}
          </AppButton>
        </div>
        ) : null}
      </main>

      <AppModal
        open={titleModalOpen}
        onClose={handleTitleModalCancel}
        title={t('task.medication.customTitleModalTitle')}
        closeAriaLabel={t('common.cancel')}
        panelMaxWidthClass="max-w-sm"
      >
        <input
          value={titleModalDraft}
          onChange={(e) => setTitleModalDraft(e.target.value)}
          className="mt-1 w-full min-h-11 rounded-lg border border-gray-300 px-3 dark:border-gray-600 dark:bg-gray-800"
          maxLength={100}
        />
        <div className="mt-4 flex gap-3">
          <AppButton type="button" variant="secondary" onClick={handleTitleModalCancel} className="flex-1">
            {t('common.cancel')}
          </AppButton>
          <AppButton type="button" variant="primary" onClick={handleTitleModalConfirm} className="flex-1">
            {t('common.confirm')}
          </AppButton>
        </div>
      </AppModal>

      <AppModal
        open={titleChoiceModalOpen}
        onClose={() => setTitleChoiceModalOpen(false)}
        title={t('task.medication.titleChoiceTitle')}
        closeAriaLabel={t('common.cancel')}
      >
        <p className="text-sm text-gray-700 dark:text-gray-300">
          {originalSharedTitle} → {autoTitle}
        </p>
        <div className="mt-4 flex flex-col gap-2">
          <AppButton
            type="button"
            variant="secondary"
            fullWidth
            onClick={() => {
              setSharedTitleOverride(originalSharedTitle);
              setTitleChoiceModalOpen(false);
              setWizardStep(5);
            }}
          >
            {t('task.medication.titleChoiceKeep')}
          </AppButton>
          <AppButton
            type="button"
            variant="primary"
            fullWidth
            onClick={() => {
              setSharedTitleOverride('');
              setTitleChoiceModalOpen(false);
              setWizardStep(5);
            }}
          >
            {t('task.medication.titleChoiceUseNew')}
          </AppButton>
        </div>
      </AppModal>

      {step3ReminderAskOpen ? (
        <HelperDueReminderLimitationModal
          open={step3ReminderAskOpen}
          title={t('task.medication.step3ReminderAskTitle')}
          introText={limitationCopy.introText}
          limitation1={limitationCopy.limitation1}
          limitation2={limitationCopy.limitation2}
          limitation3={limitationCopy.limitation3}
          confirmLabel={t('task.medication.step3ReminderAskYes')}
          cancelLabel={t('task.medication.step3ReminderAskNo')}
          onConfirm={handleStep3ReminderYes}
          onCancel={handleStep3ReminderNo}
        />
      ) : null}
    </div>
  );
};
